import { makeAutoObservable, runInAction } from 'mobx';
import { Config, Domain, DomainProductLine, InputFilter } from 'types/domainService';
import apolloClient from 'api/apolloClient';
import { GET_DOMAIN_BY_ORIGIN, DOMAIN_PRODUCT_LINES, DOMAIN_V2_CONFIG_BY_DATE } from 'api/domainService';
import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import {
  sortByProductLineId,
  PRODUCT_LINES_CONTENT_MAP,
} from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';

export const INITIAL_DOMAIN_OBJECT: Readonly<Domain> = Object.freeze({
  id: '',
  name: '',
  logo: '',
  headerStyleId: 'default',
  properties: {
    effectiveDatesAmount: 0,
    retroQuotingDaysAmount: 0,
    retroEstoreQuotingDaysAmount: 0,
    retroActiveDaysAmount: 0,
    productsAvailableYear: 0,
    productsAvailableMonth: 0,
    eligibleStates: [],
    eStoreLogo: '',
    eStoreBackground: '',
    eStoreDisclaimers: { default: '', mostPopular: '' },
    eStoreEnrollAvailable: true,
    newProposalAdditionalInputs: {
      required: [],
      optional: [],
    },
  },
  dateDependent: [],
});

export const INITIAL_DOMAIN_CONFIGURATION: Readonly<Config> = Object.freeze({
  GROUP: {
    editEeFormId: '',
    eStoreNotes: [],
    eStoreVisible: true,
    eStoreRedirect: '',
  },
});

export type ChoiceModalType = 'CONTACT_ME' | 'ENROLL_NOW' | 'SAVE_CART';

export class DomainStore {
  domainHeader: Domain = { ...INITIAL_DOMAIN_OBJECT };
  loading = true;
  // These properties are used for showing cart link and redirection
  cartItemsQuantity = 0;
  cartId = '';
  groupId = '';
  showCartLink = false;
  showContactMeModal = false;
  showChoiceModal = false;
  choiceModalType: ChoiceModalType | 'ALL' | null = null;

  public get mostPopularDisclaimer(): string | undefined {
    return this.domainHeader.properties?.eStoreDisclaimers?.mostPopular;
  }

  public get defaultDisclaimer(): string | undefined {
    return this.domainHeader.properties?.eStoreDisclaimers?.default;
  }

  public changeCartItemsQuantity(value: number): void {
    this.cartItemsQuantity = value;
  }

  public openChoiceModal(type: ChoiceModalType | 'ALL'): void {
    // Reset choiceModalType on modal open to prevent issues with hidden toasts and notifications
    this.choiceModalType = null;

    this.choiceModalType = type;
    this.showChoiceModal = true;
  }

  public closeChoiceModal(): void {
    this.showChoiceModal = false;
  }

  public setDataForCartRedirect(cartId: string, groupId: string, showCartLink = false): void {
    this.cartId = cartId;
    this.groupId = groupId;
    if (showCartLink) {
      this.showCartLink = true;
    }
  }

  public async getDomainByOrigin(origin: string): Promise<void> {
    const res = await apolloClient.query<{ getDomainByOrigin: Domain }>({
      query: GET_DOMAIN_BY_ORIGIN,
      variables: { origin },
    });

    runInAction(() => {
      this.domainHeader = res.data.getDomainByOrigin;
      this.loading = false;
    });
  }

  // eslint-disable-next-line class-methods-use-this
  public async getDomainProductLine(inputFilter: InputFilter): Promise<DomainProductLine[]> {
    return (
      await apolloClient.query<{ getProductLines: DomainProductLine[] }>({
        query: DOMAIN_PRODUCT_LINES,
        variables: { inputFilter },
        fetchPolicy: 'no-cache',
      })
    ).data.getProductLines;
  }

  // eslint-disable-next-line class-methods-use-this
  public async getDomainProductLineCached(inputFilter: InputFilter): Promise<DomainProductLine[]> {
    return (
      await apolloClient.query<{ getProductLines: DomainProductLine[] }>({
        query: DOMAIN_PRODUCT_LINES,
        variables: { inputFilter },
        fetchPolicy: 'cache-first',
      })
    ).data.getProductLines;
  }

  // eslint-disable-next-line class-methods-use-this
  public async getConfigByDate(id: string, effectiveDate: string): Promise<Config> {
    return (
      await apolloClient.query<{ getConfigByEffectiveDate: Config }>({
        query: DOMAIN_V2_CONFIG_BY_DATE,
        variables: {
          id,
          effectiveDate,
        },
      })
    ).data.getConfigByEffectiveDate;
  }

  public isEligibleState(stateFieldValue: string): boolean {
    return (
      !this.domainHeader.properties.eligibleStates ||
      this.domainHeader.properties.eligibleStates?.includes(stateFieldValue)
    );
  }

  // eslint-disable-next-line class-methods-use-this
  public getProductLineOptions(productLines: DomainProductLine[]): OptionType[] {
    return (
      productLines
        .sort((a, b) => {
          return sortByProductLineId(a.productLineId, b.productLineId);
        })
        .map((productLine) => {
          return {
            value: productLine.productLineId,
            label: PRODUCT_LINES_CONTENT_MAP[productLine.productLineId]?.label,
          };
        }) || []
    );
  }

  // eslint-disable-next-line class-methods-use-this
  public getEStorePrerequisiteFormId(productLines: DomainProductLine[], productLineId: ProductLineId): string {
    const currentPLine = productLines.find((pLine) => pLine.productLineId === productLineId);
    return currentPLine?.eStorePrerequisiteFormId || '';
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
